import React, { useMemo } from 'react';
import { Check, Sparkles } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { useTierDefaults } from '../../hooks/use-tier-defaults';
import {
  FREE_TIER_FEATURE_ORDER,
  getTierPlanFeatures,
  PRO_TIER_FEATURE_ORDER,
} from '../../utils/tier-plan-features';
import { FALLBACK_TIER_DEFAULTS } from '../../data/fallback-tier-defaults';
import { Button } from '../ui/button';
import { trackCtaClicked } from '../../utils/analytics';

const scrollToCTA = () =>
  document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' });

const GREEN = 'var(--scribane-green)';
const BLACK = 'rgb(28,28,30)';
const CARD_RADIUS = '20px';
const P_SMALL: React.CSSProperties = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 400,
  letterSpacing: '0px',
};

export const Pricing: React.FC = () => {
  const { t, locale } = useLocale();
  const { tierDefaults } = useTierDefaults();

  const resolvedTier = tierDefaults ?? FALLBACK_TIER_DEFAULTS;

  const freeFeatures = useMemo(
    () => getTierPlanFeatures(resolvedTier.free, FREE_TIER_FEATURE_ORDER, t, locale),
    [resolvedTier, t, locale],
  );

  const proFeatures = useMemo(
    () => getTierPlanFeatures(resolvedTier.pro, PRO_TIER_FEATURE_ORDER, t, locale),
    [resolvedTier, t, locale],
  );

  const editorialFeatures = [
    'landing_pricing_team_feature_1',
    'landing_pricing_team_feature_2',
    'landing_pricing_team_feature_3',
    'landing_pricing_team_feature_4',
  ] as const;

  return (
    <section
      id="pricing"
      className="scroll-mt-24 bg-white"
      style={{ padding: '110px 0 120px' }}
    >
      <div style={{ width: '100%', maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>

        {/* Section header */}
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <p style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: '16px',
            lineHeight: '20px',
            fontWeight: 500,
            letterSpacing: '3.2px',
            textTransform: 'uppercase',
            color: GREEN,
            margin: '0 0 22px',
          }}>
            {t('landing_pricing_aux')}
          </p>
          <h2 style={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontWeight: 400,
            fontSize: 'clamp(34px, 5vw, 56px)',
            lineHeight: 1.06,
            letterSpacing: '-0.02em',
            margin: 0,
            color: 'rgb(39,39,41)',
          }}>
            <strong style={{ fontWeight: 800 }}>{t('landing_pricing_title_bold')}</strong>
            {' '}{t('landing_pricing_title_light')}
          </h2>
        </div>

        <style>{`
          .pricing-card {
            transition: transform 220ms cubic-bezier(0.22,1,0.36,1),
                        box-shadow 220ms cubic-bezier(0.22,1,0.36,1),
                        border-color 220ms;
          }
          .pricing-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 32px rgba(39,39,41,0.10), 0 2px 6px rgba(39,39,41,0.05);
            border-color: rgba(39,39,41,0.18);
          }
          .pricing-card-pro:hover {
            transform: translateY(-4px);
            box-shadow: 0 32px 72px rgba(0,0,0,0.30), 0 8px 16px rgba(0,0,0,0.12);
          }
        `}</style>
        {/* Plans grid */}
        <div className="grid grid-cols-1 gap-7 max-w-[520px] mx-auto min-[800px]:grid-cols-2 min-[800px]:max-w-none min-[800px]:mx-0 min-[1000px]:grid-cols-3 min-[1000px]:items-stretch">

          {/* ── GRATUITO ── */}
          <div className="pricing-card" style={{
            border: '1px solid #e7e7e8',
            borderRadius: CARD_RADIUS,
            padding: '34px 32px',
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <p style={{ fontSize: '19px', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', margin: '0 0 14px', color: 'rgb(39,39,41)' }}>
              {t('landing_pricing_free_title')}
            </p>
            <p style={{ ...P_SMALL, color: '#686869', margin: '0 0 26px' }}>
              {t('landing_pricing_free_description')}
            </p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '26px' }}>
              <span style={{ fontSize: '46px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1, color: 'rgb(39,39,41)' }}>
                {t('landing_pricing_free_price')}
              </span>
              <span style={{ fontSize: '16px', fontWeight: 500, color: '#a9a9a9' }}>
                {t('landing_pricing_free_period')}
              </span>
            </div>
            <ul style={{ listStyle: 'none', margin: '0 0 28px', padding: 0, display: 'flex', flexDirection: 'column', gap: '15px', flex: 1 }}>
              {freeFeatures.map((feature) => (
                <li key={feature} style={{ display: 'flex', gap: '11px', alignItems: 'flex-start', ...P_SMALL, color: 'rgb(39,39,41)' }}>
                  <Check size={15} style={{ color: 'rgb(39,39,41)', flexShrink: 0, marginTop: '2px' }} />
                  {feature}
                </li>
              ))}
            </ul>
            <Button variant="outline" className="w-full" onClick={() => { trackCtaClicked(t('landing_pricing_cta'), 'pricing'); scrollToCTA(); }}>
              {t('landing_pricing_cta')}
            </Button>
          </div>

          {/* ── PRO (featured) ── */}
          <div className="pricing-card pricing-card-pro" style={{
            background: BLACK,
            borderRadius: CARD_RADIUS,
            padding: '34px 32px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 24px 60px rgba(0,0,0,.22)',
          }}>
            {/* Badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', alignSelf: 'flex-start',
              background: GREEN, color: '#0f3d20', fontWeight: 700, fontSize: '13px',
              padding: '7px 14px', borderRadius: '999px', marginBottom: '18px',
            }}>
              <Sparkles size={14} />
              {t('landing_pricing_pro_badge')}
            </div>

            <p style={{ fontSize: '19px', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', margin: '0 0 14px', color: '#fff' }}>
              {t('landing_pricing_pro_title')}
            </p>
            <p style={{ ...P_SMALL, color: '#c5c5c7', margin: '0 0 10px' }}>
              {t('landing_pricing_pro_subtitle')}
            </p>
            <p style={{ ...P_SMALL, color: '#c5c5c7', margin: '0 0 26px' }}>
              {t('landing_pricing_pro_description')}
            </p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '26px' }}>
              <span style={{ fontSize: '46px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1, color: '#fff' }}>
                {t('landing_pricing_pro_price')}
              </span>
              <span style={{ fontSize: '16px', fontWeight: 500, color: '#9a9a9c' }}>
                {t('landing_pricing_pro_period')}
              </span>
            </div>
            <ul style={{ listStyle: 'none', margin: '0 0 28px', padding: 0, display: 'flex', flexDirection: 'column', gap: '15px', flex: 1 }}>
              {proFeatures.map((feature) => (
                <li key={feature} style={{ display: 'flex', gap: '11px', alignItems: 'flex-start', ...P_SMALL, color: '#d7d7d9' }}>
                  <Check size={15} style={{ color: GREEN, flexShrink: 0, marginTop: '2px' }} />
                  {feature}
                </li>
              ))}
            </ul>
            <Button variant="primary" className="w-full" onClick={() => { trackCtaClicked(t('landing_pricing_pro_cta'), 'pricing'); scrollToCTA(); }}>
              {t('landing_pricing_pro_cta')}
            </Button>
          </div>

          {/* ── EDITORIAL ── */}
          <div className="pricing-card" style={{
            border: '1px solid #e7e7e8',
            borderRadius: CARD_RADIUS,
            padding: '34px 32px',
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <p style={{ fontSize: '19px', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', margin: '0 0 14px', color: 'rgb(39,39,41)' }}>
              {t('landing_pricing_team_title')}
            </p>
            <p style={{ ...P_SMALL, color: '#686869', margin: '0 0 26px' }}>
              {t('landing_pricing_team_description')}
            </p>
            <ul style={{ listStyle: 'none', margin: '0 0 28px', padding: 0, display: 'flex', flexDirection: 'column', gap: '15px', flex: 1 }}>
              {editorialFeatures.map((key) => (
                <li key={key} style={{ display: 'flex', gap: '11px', alignItems: 'flex-start', ...P_SMALL, color: 'rgb(39,39,41)' }}>
                  <Check size={15} style={{ color: 'rgb(39,39,41)', flexShrink: 0, marginTop: '2px' }} />
                  {t(key)}
                </li>
              ))}
            </ul>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => { trackCtaClicked(t('landing_pricing_team_cta'), 'pricing'); document.getElementById('footer')?.scrollIntoView({ behavior: 'smooth' }); }}
            >
              {t('landing_pricing_team_cta')}
            </Button>
          </div>

        </div>

      </div>
    </section>
  );
};
